import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { IntegratedConversationItem } from './IntegratedConversationItem';
import AnalyticsApiService, { PageParams, PaginatedConversations } from '../services/analyticsApi';
import { transformBackendConversation as transformConvShared } from '../utils/conversationTransform';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { conversationCache } from '../services/conversationCache';
import { Participant } from '../App';
import { AppUsersSearchMode } from './AppUsersSearchMode';
import ExpandedConversationModal from './ExpandedConversationModal';

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isOwn: boolean;
  createdAt: Date;
  sender?: string;
  senderId?: string;
  senderName?: string;
  messageHandle?: string;
  runId?: string;
  metadata?: Record<string, any>;
}

interface Conversation {
  id: string;
  name: string;
  phoneNumber: string;
  userPhoneNumber: string;
  profileImage?: string;
  username?: string;
  messages: Message[];
  isGroup: boolean;
  lastMessageTime: Date;
  participants?: Participant[];
  unread?: boolean;
  lastReadAt?: Date | null;
}

interface SearchPageProps {
  onSendMessage: (conversationId: string, message: string) => void;
  onLiveMessage?: (conversationId: string, messageData: any) => void;
  onClose: () => void;
}

export function SearchPage({ onSendMessage, onLiveMessage, onClose }: SearchPageProps) {
  const [searchMode, setSearchMode] = useState<'conversations' | 'users'>('conversations');
  const isUsersMode = searchMode === 'users';
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [expandedConversationId, setExpandedConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullHistoryConversations, setFullHistoryConversations] = useState<Set<string>>(new Set());
  
  const currentPageRef = useRef(1);
  const loadedConversationIds = useRef<Set<number>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hydratedConversationIds = useRef<Set<string>>(new Set());
  
  const BATCH_SIZE = 25;

  const handleOpenConversationSearch = useCallback((search: string) => {
    const trimmed = search.trim();
    if (!trimmed) {
      setSearchMode('conversations');
      // Focus once conversations UI mounts
      setTimeout(() => searchInputRef.current?.focus(), 50);
      return;
    }

    setSearchQuery(trimmed);
    setSearchMode('conversations');
    // Focus once conversations UI mounts
    setTimeout(() => searchInputRef.current?.focus(), 50);
  }, []);

  // Auto-focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Transform backend conversation data
  const transformBackendConversation = useCallback((backendConv: any): Conversation => {
    return transformConvShared(backendConv) as unknown as Conversation;
  }, []);

  // Merge messages helper
  const mergeMessages = useCallback((oldMsgs: Message[], incomingMsgs: Message[]): Message[] => {
    const byId = new Map<string, Message>();
    
    // Add old messages
    oldMsgs.forEach(m => byId.set(m.id, m));
    
    // Add/update with incoming messages
    incomingMsgs.forEach(m => byId.set(m.id, m));
    
    const merged = Array.from(byId.values());
    merged.sort((a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0));
    
    return merged;
  }, []);

  // Load search results
  const loadSearchResults = useCallback(async (shouldAppend: boolean = false) => {
    if (!debouncedSearchQuery || debouncedSearchQuery.trim().length === 0) {
      // Clear results if search is empty
      setConversations([]);
      setHasMore(false);
      return;
    }

    try {
      const currentPage = shouldAppend ? currentPageRef.current : 1;
      
      if (shouldAppend) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError(null);
        loadedConversationIds.current.clear();
      }
      
      console.log(`🔍 Searching for "${debouncedSearchQuery}", page: ${currentPage}`);
      
      const response = await AnalyticsApiService.getConversations({
        page: currentPage,
        limit: BATCH_SIZE,
        search: debouncedSearchQuery.trim()
      });
      
      if (!response || !response.items || response.items.length === 0) {
        if (!shouldAppend) {
          setConversations([]);
        }
        setHasMore(false);
        return;
      }
      
      const transformed = response.items.map(transformBackendConversation);
      
      // Deduplicate
      const newConversations = transformed.filter((conv: any) => {
        const convId = typeof conv.id === 'number' ? conv.id : parseInt(conv.id, 10);
        if (loadedConversationIds.current.has(convId)) {
          return false;
        }
        loadedConversationIds.current.add(convId);
        return true;
      });
      
      if (shouldAppend) {
        setConversations(prev => [...prev, ...newConversations]);
      } else {
        setConversations(newConversations);
      }
      
      currentPageRef.current = currentPage + 1;
      setHasMore(response.items.length >= BATCH_SIZE);
      
      console.log(`✅ Found ${newConversations.length} conversations matching "${debouncedSearchQuery}"`);
    } catch (err) {
      console.error('Search failed:', err);
      setError(err instanceof Error ? err.message : 'Search failed');
      if (!shouldAppend) {
        setConversations([]);
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [debouncedSearchQuery, transformBackendConversation, BATCH_SIZE]);

  // Trigger search when debounced query changes
  useEffect(() => {
    currentPageRef.current = 1;
    loadSearchResults(false);
  }, [debouncedSearchQuery, loadSearchResults]);

  // Load more for infinite scroll
  const loadMore = useCallback(async () => {
    if (!isLoadingMore && hasMore) {
      await loadSearchResults(true);
    }
  }, [isLoadingMore, hasMore, loadSearchResults]);

  // Infinite scroll hook
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: isLoadingMore,
    enabled: conversations.length > 0,
  });

  // Handle conversation activation
  const handleConversationClick = useCallback(async (conversationId: string) => {
    const conversationIdNumber = parseInt(conversationId, 10);
    
    // Toggle
    if (activeConversationId === conversationIdNumber) {
      setActiveConversationId(null);
      return;
    }
    
    setActiveConversationId(conversationIdNumber);
    console.log(`⚡ Activated conversation ${conversationId} in search results`);
    
    // Fetch full conversation if not hydrated
    if (!hydratedConversationIds.current.has(conversationId)) {
      const cached = conversationCache.get(conversationIdNumber);
      
      if (cached) {
        const transformed = transformBackendConversation(cached);
        setConversations(prev => prev.map(c => {
          if (c.id !== conversationId) return c;
          const merged = mergeMessages(c.messages, transformed.messages);
          return { ...transformed, messages: merged };
        }));
        hydratedConversationIds.current.add(conversationId);
      } else {
        AnalyticsApiService.getConversation(conversationIdNumber)
          .then(fresh => {
            conversationCache.set(conversationIdNumber, fresh);
            const transformed = transformBackendConversation(fresh);
            setConversations(prev => prev.map(c => {
              if (c.id !== conversationId) return c;
              const merged = mergeMessages(c.messages, transformed.messages);
              return { ...transformed, messages: merged };
            }));
            hydratedConversationIds.current.add(conversationId);
          })
          .catch(console.warn);
      }
    }
  }, [activeConversationId, transformBackendConversation, mergeMessages]);

  // Handle toggle full history
  const handleToggleFullHistory = useCallback(async (conversationId: string, enabled: boolean) => {
    setFullHistoryConversations(prev => {
      const newSet = new Set(prev);
      if (enabled) {
        newSet.add(conversationId);
      } else {
        newSet.delete(conversationId);
      }
      return newSet;
    });

    try {
      const convIdNumber = parseInt(conversationId, 10);
      const fresh = await AnalyticsApiService.getConversation(convIdNumber, enabled);
      const transformed = transformBackendConversation(fresh);
      
      setConversations(prev => prev.map(c => {
        if (c.id !== conversationId) return c;
        return { ...c, ...transformed, messages: transformed.messages };
      }));
      
      hydratedConversationIds.current.add(conversationId);
    } catch (error) {
      console.error(`Failed to reload conversation ${conversationId}:`, error);
    }
  }, [transformBackendConversation]);

  const handleExpandConversation = useCallback(
    async (conversationId: string) => {
      setExpandedConversationId(conversationId);

      // Best-effort hydration so the expanded modal opens with full history
      if (hydratedConversationIds.current.has(conversationId)) return;

      const conversationIdNumber = parseInt(conversationId, 10);
      if (!Number.isFinite(conversationIdNumber)) return;

      const cached = conversationCache.get(conversationIdNumber);
      if (cached) {
        const transformed = transformBackendConversation(cached);
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== conversationId) return c;
            const merged = mergeMessages(c.messages, transformed.messages);
            return { ...transformed, messages: merged };
          }),
        );
        hydratedConversationIds.current.add(conversationId);
        return;
      }

      try {
        const fresh = await AnalyticsApiService.getConversation(conversationIdNumber);
        conversationCache.set(conversationIdNumber, fresh);
        const transformed = transformBackendConversation(fresh);
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== conversationId) return c;
            const merged = mergeMessages(c.messages, transformed.messages);
            return { ...transformed, messages: merged };
          }),
        );
        hydratedConversationIds.current.add(conversationId);
      } catch (err) {
        console.warn('Failed to hydrate conversation for expand modal:', err);
      }
    },
    [mergeMessages, transformBackendConversation],
  );

  const handleCloseExpanded = useCallback(() => {
    setExpandedConversationId(null);
  }, []);

  const expandedConversation = useMemo(() => {
    if (!expandedConversationId) return null;
    return conversations.find((c) => c.id === expandedConversationId) || null;
  }, [conversations, expandedConversationId]);

  if (isUsersMode) {
    return (
      <AppUsersSearchMode
        onClose={onClose}
        onModeChange={setSearchMode}
        onOpenConversationSearch={handleOpenConversationSearch}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Search Header */}
      <div className="px-8 pt-6 pb-5 bg-gradient-to-b from-white/70 via-white/50 to-transparent dark:from-gray-900/70 dark:via-gray-900/50 dark:to-transparent backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center gap-4">
          {/* Back button */}
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-md hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-200"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Search input */}
          <div className="flex-1 relative bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center h-12 px-4">
              <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, phone, or conversation..."
                className="flex-1 bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-base font-['SF_Pro_Display',_sans-serif] outline-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Mode toggle */}
          <div className="shrink-0 flex items-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/60 backdrop-blur-md p-1">
            <button
              type="button"
              onClick={() => setSearchMode('conversations')}
              aria-label="Conversation search mode"
              aria-pressed={!isUsersMode}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                !isUsersMode
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800',
              )}
            >
              Conversations
            </button>
            <button
              type="button"
              onClick={() => setSearchMode('users')}
              aria-label="User search mode"
              aria-pressed={isUsersMode}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                isUsersMode
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800',
              )}
            >
              Users
            </button>
          </div>
        </div>

        {/* Search stats */}
        {!isLoading && conversations.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Found {conversations.length} conversation{conversations.length !== 1 ? 's' : ''} matching "{debouncedSearchQuery}"
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}
      </div>

      {/* Search Results */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-8 py-8">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-700 border-t-gray-600 dark:border-t-gray-400 rounded-full animate-spin mx-auto"></div>
                <p className="text-gray-600 dark:text-gray-400">
                  Searching for "{debouncedSearchQuery}"...
                </p>
              </div>
            </div>
          ) : !debouncedSearchQuery || debouncedSearchQuery.trim().length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div className="space-y-2">
                  <h3 className="font-['SF_Pro_Display',_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xl">
                    Start searching
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Type a name, phone number, or conversation to search
                  </p>
                </div>
              </div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div className="space-y-2">
                  <h3 className="font-['SF_Pro_Display',_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xl">
                    No results found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    No conversations match "{debouncedSearchQuery}". Try different keywords.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="space-y-10">
                {conversations.map((conversation) => (
                  <div key={conversation.id} data-conversation-id={conversation.id}>
                    <IntegratedConversationItem
                      conversation={conversation}
                      onSendMessage={onSendMessage}
                      onLiveMessage={onLiveMessage}
                      onConversationClick={handleConversationClick}
                      onExpandClick={handleExpandConversation}
                      onToggleFullHistory={handleToggleFullHistory}
                      isActiveConversation={activeConversationId !== null && activeConversationId === parseInt(conversation.id, 10)}
                      showFullHistory={fullHistoryConversations.has(conversation.id)}
                    />
                  </div>
                ))}
              </div>

              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Loading more...</span>
                  </div>
                </div>
              )}

              {/* Sentinel for infinite scroll */}
              <div ref={sentinelRef} className="h-4" />

              {/* End indicator */}
              {!hasMore && conversations.length > 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  {conversations.length} result{conversations.length !== 1 ? 's' : ''} found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded Conversation Modal */}
      <ExpandedConversationModal
        isOpen={!!expandedConversationId}
        onClose={handleCloseExpanded}
        conversation={expandedConversation}
        onSendMessage={onSendMessage}
        onLiveMessage={onLiveMessage}
      />
    </div>
  );
}

